import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  CalendarCheck, Plus, Phone, ChevronLeft, ChevronRight,
  UserCheck, XCircle, GraduationCap, Loader2, CheckCircle,
  AlertTriangle, ClipboardCheck, Search, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { useCelebration } from '@/hooks/useCelebration';
import LeadDetailDrawer from '@/components/training/LeadDetailDrawer';
import ClassDetailDialog from '@/components/training/ClassDetailDialog';

const STATUS_CONFIG = {
  confirmed: { label: 'Booked In', color: 'bg-blue-100 text-blue-700', icon: CalendarCheck },
  attended: { label: 'Attended', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  enrolled: { label: 'Enrolled', color: 'bg-pink-100 text-pink-700', icon: GraduationCap },
  no_show: { label: 'No Show', color: 'bg-red-100 text-red-700', icon: XCircle },
  rescheduled: { label: 'Rescheduled', color: 'bg-amber-100 text-amber-700', icon: CalendarCheck },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: XCircle },
  registration_failed: { label: 'Not Eligible', color: 'bg-red-100 text-red-600', icon: AlertTriangle },
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getRange = (mode, anchor) => {
  const a = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  if (mode === 'day') return { rangeStart: a, rangeEnd: a };
  if (mode === 'week') {
    const start = new Date(a);
    start.setDate(a.getDate() - ((a.getDay() + 6) % 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { rangeStart: start, rangeEnd: end };
  }
  return { rangeStart: new Date(a.getFullYear(), a.getMonth(), 1), rangeEnd: new Date(a.getFullYear(), a.getMonth() + 1, 0) };
};

const RegistrationsPage = () => {
  const { selectedBusiness } = useAuth();
  const { celebrate } = useCelebration();
  const [registrations, setRegistrations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [ghlEvents, setGhlEvents] = useState([]);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [ghlCalendars, setGhlCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');
  const [classDetail, setClassDetail] = useState(null);

  // Complete registration dialog state
  const [completeDialog, setCompleteDialog] = useState(false);
  const [selectedReg, setSelectedReg] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [eligibilityPassed, setEligibilityPassed] = useState(true);
  const [testPassed, setTestPassed] = useState(true);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  // Add registration dialog state
  const [addDialog, setAddDialog] = useState(false);
  const [addingReg, setAddingReg] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [addRegForm, setAddRegForm] = useState({
    lead_id: '', lead_name: '', registration_date: '', registration_time: '', course_interest: '', notes: '',
  });

  // Lead drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const { rangeStart, rangeEnd } = getRange(viewMode, anchor);
  const rangeStartStr = toDateStr(rangeStart);
  const rangeEndStr = toDateStr(rangeEnd);

  const fetchRegistrations = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/training/registrations?start_date=${rangeStartStr}&end_date=${rangeEndStr}`,
        { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }
      );
      if (res.ok) setRegistrations(await res.json());
    } catch (err) {
      console.error('Registrations fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [rangeStartStr, rangeEndStr, token]);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/courses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) setCourses(await res.json());
    } catch (err) {
      console.error('Courses fetch error:', err);
    }
  }, [token]);

  const fetchGhlEvents = useCallback(async () => {
    try {
      const startTime = new Date(`${rangeStartStr}T00:00:00`).toISOString();
      const endTime = new Date(`${rangeEndStr}T23:59:59`).toISOString();

      const res = await fetch(
        `${API_URL}/api/training/ghl/calendar-events?start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`,
        { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setGhlEvents(data.ghl_events || []);
        setGhlConnected(data.synced === true);
      }
    } catch (err) {
      console.error('GHL events fetch error:', err);
    }
  }, [rangeStartStr, rangeEndStr, token]);

  const fetchGhlCalendars = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/ghl/calendars`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setGhlCalendars(data.calendars || []);
        setGhlConnected(true);
      }
    } catch (err) {
      // Not connected — that's fine
    }
  }, [token]);

  useEffect(() => {
    fetchRegistrations();
    fetchCourses();
    fetchGhlEvents();
    fetchGhlCalendars();
  }, [fetchRegistrations, fetchCourses, fetchGhlEvents, fetchGhlCalendars, selectedBusiness?.business_id]);

  const todayStr = toDateStr(new Date());

  // Keep a sensible day selected when the view or range changes
  useEffect(() => {
    if (viewMode === 'day') {
      setSelectedDay(toDateStr(anchor));
    } else {
      setSelectedDay(todayStr >= rangeStartStr && todayStr <= rangeEndStr ? todayStr : (viewMode === 'week' ? rangeStartStr : null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, rangeStartStr, rangeEndStr]);

  const shiftRange = (dir) => {
    const d = new Date(anchor);
    if (viewMode === 'month') d.setMonth(d.getMonth() + dir, 1);
    else d.setDate(d.getDate() + (viewMode === 'week' ? 7 : 1) * dir);
    setAnchor(d);
  };
  const goToToday = () => setAnchor(new Date());

  const rangeTitle = viewMode === 'month'
    ? anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : viewMode === 'week'
      ? `${rangeStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${rangeEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : anchor.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Days shown in the current view (month grid gets leading blanks so it starts on Monday)
  const rangeDays = [];
  for (let d = new Date(rangeStart); d <= rangeEnd; d.setDate(d.getDate() + 1)) rangeDays.push(toDateStr(d));
  const cells = viewMode === 'month' ? [...Array((rangeStart.getDay() + 6) % 7).fill(null), ...rangeDays] : rangeDays;

  const filteredCourse = courseFilter === 'all' ? null : courses.find(c => c.course_id === courseFilter);
  const matchesCourse = (text) => {
    if (!filteredCourse) return true;
    const a = (text || '').toLowerCase();
    const b = (filteredCourse.name || '').toLowerCase();
    return !!a && (a.includes(b) || b.includes(a));
  };

  // ── Build event maps ──
  const regsByDate = {};
  registrations.filter(reg => matchesCourse(reg.course_interest)).forEach(reg => {
    const date = reg.registration_date;
    if (!regsByDate[date]) regsByDate[date] = [];
    regsByDate[date].push(reg);
  });

  const classDayMap = {};
  courses.forEach(course => {
    if (!course.class_days || course.status === 'completed') return;
    if (filteredCourse && course.course_id !== filteredCourse.course_id) return;
    const days = course.class_days.map(d => d.toLowerCase());
    rangeDays.forEach(dateStr => {
      if (dateStr < course.start_date || dateStr > course.end_date) return;
      const dayName = WEEKDAY_NAMES[new Date(`${dateStr}T00:00:00`).getDay()];
      if (!days.includes(dayName)) return;
      const t = course.day_schedule?.[dayName] || {};
      if (!classDayMap[dateStr]) classDayMap[dateStr] = [];
      classDayMap[dateStr].push({
        type: 'class',
        course_name: course.name,
        course_id: course.course_id,
        start: t.start || course.class_time_start,
        time: `${t.start || course.class_time_start} - ${t.end || course.class_time_end}`,
        location: course.location,
        accreditation: course.accreditation,
        date: dateStr,
      });
    });
  });
  Object.values(classDayMap).forEach(list => list.sort((a, b) => (a.start || '').localeCompare(b.start || '')));

  const ghlByDate = {};
  if (!filteredCourse) {
    ghlEvents.forEach(evt => {
      if (!evt.start_time) return;
      const dateStr = toDateStr(new Date(evt.start_time));
      if (!ghlByDate[dateStr]) ghlByDate[dateStr] = [];
      ghlByDate[dateStr].push(evt);
    });
  }

  // ── Selected day events ──
  const selectedRegs = selectedDay ? (regsByDate[selectedDay] || []) : [];
  const selectedClasses = selectedDay ? (classDayMap[selectedDay] || []) : [];
  const selectedGhlEvents = selectedDay ? (ghlByDate[selectedDay] || []) : [];
  const hasSelectedEvents = selectedRegs.length > 0 || selectedClasses.length > 0 || selectedGhlEvents.length > 0;

  // Summary stats
  const totalBooked = registrations.filter(r => r.status === 'confirmed').length;
  const totalAttended = registrations.filter(r => r.status === 'attended' || r.status === 'enrolled').length;
  const totalNoShow = registrations.filter(r => r.status === 'no_show').length;

  const handleStatusUpdate = async (regId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/training/registrations/${regId}/status`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Registration marked as ${newStatus.replace('_', ' ')}`);
        fetchRegistrations();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const openCompleteDialog = (reg) => {
    setSelectedReg(reg);
    setSelectedCourseId('');
    setEligibilityPassed(true);
    setTestPassed(true);
    setCompleteNotes('');
    setCompleteDialog(true);
  };

  const handleCompleteRegistration = async () => {
    if (!selectedCourseId) {
      toast.error('Please select a course');
      return;
    }
    setCompleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/training/registrations/${selectedReg.registration_id}/complete`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            registration_id: selectedReg.registration_id,
            course_id: selectedCourseId,
            eligibility_passed: eligibilityPassed,
            test_passed: testPassed,
            notes: completeNotes || null,
          }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        if (data.status === 'enrolled') {
          celebrate();
          toast.success(`${selectedReg.lead_name} enrolled into ${data.course_name}!`);
        } else if (data.status === 'failed') {
          toast.error(`Registration failed: ${data.reason.replace('_', ' ')}`);
        }
        setCompleteDialog(false);
        fetchRegistrations();
      } else {
        toast.error(data.detail || 'Failed to complete registration');
      }
    } catch (err) {
      toast.error('Failed to complete registration');
    } finally {
      setCompleting(false);
    }
  };

  // Search leads for add registration dialog
  const searchLeads = useCallback(async (query) => {
    if (!query || query.length < 2) { setLeadResults([]); return; }
    setSearchingLeads(true);
    try {
      const res = await fetch(`${API_URL}/api/leads?search=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLeadResults(data.slice(0, 8));
      }
    } catch (err) {
      console.error('Lead search error:', err);
    } finally {
      setSearchingLeads(false);
    }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => searchLeads(leadSearch), 300);
    return () => clearTimeout(timer);
  }, [leadSearch, searchLeads]);

  const openAddDialog = () => {
    setAddRegForm({ lead_id: '', lead_name: '', registration_date: '', registration_time: '', course_interest: '', notes: '' });
    setLeadSearch('');
    setLeadResults([]);
    setAddDialog(true);
  };

  const handleAddRegistration = async () => {
    if (!addRegForm.lead_id) { toast.error('Please select a student'); return; }
    if (!addRegForm.registration_date) { toast.error('Please select a date'); return; }
    setAddingReg(true);
    try {
      const res = await fetch(`${API_URL}/api/training/registrations`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          lead_id: addRegForm.lead_id,
          registration_date: addRegForm.registration_date,
          registration_time: addRegForm.registration_time || null,
          course_interest: addRegForm.course_interest || null,
          notes: addRegForm.notes || null,
        }),
      });
      if (res.ok) {
        celebrate();
        toast.success(`Registration booked for ${addRegForm.lead_name}`);
        setAddDialog(false);
        fetchRegistrations();

        // Push to GHL calendar if connected
        if (ghlConnected && ghlCalendars.length > 0 && addRegForm.registration_date) {
          try {
            const regDate = addRegForm.registration_date;
            const regTime = addRegForm.registration_time || '10:00';
            const startTime = new Date(`${regDate}T${regTime}:00`).toISOString();
            const endMs = new Date(`${regDate}T${regTime}:00`).getTime() + 60 * 60 * 1000;
            const endTime = new Date(endMs).toISOString();

            await fetch(`${API_URL}/api/training/ghl/calendar-events`, {
              method: 'POST',
              headers,
              credentials: 'include',
              body: JSON.stringify({
                calendar_id: ghlCalendars[0].id,
                title: `Registration - ${addRegForm.lead_name}`,
                start_time: startTime,
                end_time: endTime,
                lead_id: addRegForm.lead_id,
                notes: addRegForm.course_interest ? `Course interest: ${addRegForm.course_interest}` : undefined,
              }),
            });
            fetchGhlEvents();
          } catch (err) {
            console.error('GHL push sync error:', err);
          }
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create registration');
      }
    } catch (err) {
      toast.error('Failed to create registration');
    } finally {
      setAddingReg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-pink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Calendar
          </h1>
          <p className="text-slate-500 mt-1">Registrations and class schedules at a glance</p>
        </div>
        <Button className="btn-primary rounded-xl px-5 h-11" onClick={openAddDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Add Registration
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{totalBooked}</p>
            <p className="text-xs text-slate-500">Booked In</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{totalAttended}</p>
            <p className="text-xs text-slate-500">Attended / Enrolled</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{totalNoShow}</p>
            <p className="text-xs text-slate-500">No Shows</p>
          </CardContent>
        </Card>
      </div>

      {/* View + filter controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="inline-flex rounded-xl bg-slate-100 p-1 self-start">
          {[{ v: 'day', l: 'Day' }, { v: 'week', l: 'Week' }, { v: 'month', l: 'Month' }].map(opt => (
            <button
              key={opt.v}
              type="button"
              aria-pressed={viewMode === opt.v}
              onClick={() => setViewMode(opt.v)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === opt.v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-64 rounded-xl h-10"><SelectValue placeholder="All courses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All courses</SelectItem>
            {courses.filter(c => c.status !== 'completed').map(c => (
              <SelectItem key={c.course_id} value={c.course_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Range navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => shiftRange(-1)} className="rounded-xl" aria-label="Previous">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <h2 className="text-base sm:text-lg font-semibold text-slate-800 text-center">{rangeTitle}</h2>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={goToToday}>
            Today
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={() => shiftRange(1)} className="rounded-xl" aria-label="Next">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      {filteredCourse && (
        <p className="text-xs text-slate-500 -mt-3">Showing {filteredCourse.name} classes and registrations only.</p>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-pink-400" />
          Registrations
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
          Course Classes
        </span>
        {ghlConnected && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-orange-400" />
            Calendar Appointments
          </span>
        )}
      </div>

      {/* Calendar Grid (month / week) */}
      {viewMode !== 'day' && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-7 border-b border-slate-100">
              {DAY_NAMES.map((day, i) => (
                <div key={day} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {day}
                  {viewMode === 'week' && rangeDays[i] && (
                    <span className={`block text-sm normal-case ${rangeDays[i] === todayStr ? 'text-pink-600' : 'text-slate-800'}`}>
                      {parseInt(rangeDays[i].split('-')[2], 10)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((dateStr, idx) => {
                if (!dateStr) {
                  return <div key={`empty-${idx}`} className="min-h-[72px] sm:min-h-[96px] border-b border-r border-slate-50" />;
                }
                const dayNum = parseInt(dateStr.split('-')[2], 10);
                const dayClasses = classDayMap[dateStr] || [];
                const dayRegs = regsByDate[dateStr] || [];
                const dayGhl = ghlByDate[dateStr] || [];
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDay;
                const chipLimit = viewMode === 'week' ? 12 : 2;
                const chips = [
                  ...dayClasses.map(c => ({ key: `c-${c.course_id}`, label: `${c.start} ${c.course_name}`, cls: 'bg-blue-50 text-blue-700', onClick: () => setClassDetail(c) })),
                  ...dayRegs.map(r => ({ key: `r-${r.registration_id}`, label: `${r.registration_time || ''} ${r.lead_name || 'Registration'}`.trim(), cls: 'bg-pink-50 text-pink-700' })),
                  ...dayGhl.map((g, i) => ({ key: `g-${g.ghl_event_id || i}`, label: g.contact_name || g.title || 'Appointment', cls: 'bg-orange-50 text-orange-700' })),
                ];
                return (
                  <div
                    key={dateStr}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDay(dateStr === selectedDay && viewMode === 'month' ? null : dateStr)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDay(dateStr); } }}
                    className={`${viewMode === 'week' ? 'min-h-[180px]' : 'min-h-[72px] sm:min-h-[96px]'} p-1 sm:p-1.5 border-b border-r border-slate-50 text-left transition-colors cursor-pointer
                      ${isSelected ? 'bg-pink-50 ring-2 ring-pink-300 ring-inset' : 'hover:bg-slate-50'}
                      ${isToday && !isSelected ? 'bg-blue-50/50' : ''}`}
                  >
                    {viewMode === 'month' && (
                      <span className={`text-xs sm:text-sm font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? 'bg-pink-500 text-white' : 'text-slate-700'}`}>
                        {dayNum}
                      </span>
                    )}
                    <div className="hidden sm:flex flex-col gap-0.5 mt-0.5">
                      {chips.slice(0, chipLimit).map(ch => (
                        <span
                          key={ch.key}
                          onClick={ch.onClick ? (e) => { e.stopPropagation(); ch.onClick(); } : undefined}
                          className={`block truncate rounded px-1 py-0.5 text-[10px] leading-tight ${ch.cls} ${ch.onClick ? 'hover:brightness-95' : ''}`}
                          title={ch.label}
                        >
                          {ch.label}
                        </span>
                      ))}
                      {chips.length > chipLimit && <span className="text-[10px] text-slate-400 px-1">+{chips.length - chipLimit} more</span>}
                    </div>
                    <div className="flex sm:hidden items-center gap-1 mt-1 flex-wrap">
                      {dayRegs.length > 0 && <span className="w-2 h-2 rounded-full bg-pink-400" />}
                      {dayClasses.length > 0 && <span className="w-2 h-2 rounded-full bg-blue-400" />}
                      {dayGhl.length > 0 && <span className="w-2 h-2 rounded-full bg-orange-400" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Day Detail */}
      {selectedDay && (
        <div className="space-y-3 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-700">
            {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-GB', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </h3>

          {!hasSelectedEvents ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-8 text-center">
                <p className="text-sm text-slate-400">Nothing scheduled for this day</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Course Classes */}
              {selectedClasses.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wider flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5" />
                    Classes ({selectedClasses.length})
                  </p>
                  {selectedClasses.map((cls, idx) => (
                    <button key={idx} type="button" className="w-full text-left" onClick={() => setClassDetail(cls)}>
                      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{cls.course_name}</p>
                            <p className="text-xs text-slate-500">
                              {cls.time}
                              {cls.location && ` • ${cls.location}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {cls.accreditation && (
                              <Badge className="text-xs bg-blue-50 text-blue-600">{cls.accreditation}</Badge>
                            )}
                            <span className="text-xs font-medium text-pink-600">Details</span>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
              )}

              {/* Registrations */}
              {selectedRegs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-pink-600 uppercase tracking-wider flex items-center gap-1.5">
                    <CalendarCheck className="w-3.5 h-3.5" />
                    Registrations ({selectedRegs.length})
                  </p>
                  {selectedRegs.map((reg) => {
                    const cfg = STATUS_CONFIG[reg.status] || STATUS_CONFIG.confirmed;
                    const StatusIcon = cfg.icon;
                    return (
                      <Card key={reg.registration_id} className="border-0 shadow-sm">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <button
                                className="text-sm font-medium text-slate-900 hover:text-pink-600 transition-colors text-left"
                                onClick={() => { setDrawerLeadId(reg.lead_id); setDrawerOpen(true); }}
                              >
                                {reg.lead_name || 'Unknown'}
                              </button>
                              <p className="text-xs text-slate-500">
                                {reg.registration_time || 'Time TBC'}
                                {reg.course_interest && ` • ${reg.course_interest}`}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={`text-xs ${cfg.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {cfg.label}
                              </Badge>

                              {reg.status === 'confirmed' && (
                                <>
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => openCompleteDialog(reg)}
                                  >
                                    <ClipboardCheck className="w-3 h-3 mr-1" />
                                    Complete
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-xs rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => handleStatusUpdate(reg.registration_id, 'no_show')}
                                  >
                                    No Show
                                  </Button>
                                </>
                              )}

                              {reg.status === 'attended' && (
                                <Button
                                  size="sm"
                                  className="h-8 text-xs rounded-lg bg-pink-500 hover:bg-pink-600 text-white"
                                  onClick={() => openCompleteDialog(reg)}
                                >
                                  <GraduationCap className="w-3 h-3 mr-1" />
                                  Enrol
                                </Button>
                              )}

                              {reg.lead_phone && (
                                <a
                                  href={`tel:${reg.lead_phone}`}
                                  className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center hover:bg-pink-200 transition-colors"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* GHL Appointments */}
              {selectedGhlEvents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-orange-600 uppercase tracking-wider flex items-center gap-1.5">
                    <CalendarCheck className="w-3.5 h-3.5" />
                    Calendar Appointments ({selectedGhlEvents.length})
                  </p>
                  {selectedGhlEvents.map((evt, idx) => {
                    const startTime = evt.start_time ? new Date(evt.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                    const endTime = evt.end_time ? new Date(evt.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                    const statusColor = evt.status === 'confirmed' ? 'bg-green-100 text-green-700'
                      : evt.status === 'cancelled' ? 'bg-red-100 text-red-600'
                      : evt.status === 'showed' ? 'bg-emerald-100 text-emerald-700'
                      : evt.status === 'noshow' ? 'bg-red-100 text-red-600'
                      : 'bg-orange-100 text-orange-700';

                    return (
                      <Card key={evt.ghl_event_id || idx} className="border-0 shadow-sm">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">
                                {evt.contact_name || evt.title}
                              </p>
                              <p className="text-xs text-slate-500">
                                {startTime}{endTime ? ` - ${endTime}` : ''}
                                {evt.title && evt.contact_name && evt.title !== evt.contact_name && ` • ${evt.title}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={`text-xs ${statusColor}`}>
                                {(evt.status || 'booked').replace('_', ' ')}
                              </Badge>
                              {evt.contact_phone && (
                                <a
                                  href={`tel:${evt.contact_phone}`}
                                  className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center hover:bg-orange-200 transition-colors"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Complete Registration Dialog */}
      <Dialog open={completeDialog} onOpenChange={setCompleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Complete Registration
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              {selectedReg?.lead_name} — {selectedReg?.course_interest || 'No course specified'}
            </p>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="p-4 bg-slate-50 rounded-xl space-y-3">
              <p className="text-sm font-medium text-slate-700">Checks</p>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="eligibility"
                  checked={eligibilityPassed}
                  onCheckedChange={setEligibilityPassed}
                />
                <label htmlFor="eligibility" className="text-sm text-slate-600 cursor-pointer">
                  Eligibility check passed
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="test"
                  checked={testPassed}
                  onCheckedChange={setTestPassed}
                />
                <label htmlFor="test" className="text-sm text-slate-600 cursor-pointer">
                  Assessment / test passed
                </label>
              </div>
            </div>

            {eligibilityPassed && testPassed && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Enrol into course</label>
                <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.filter(c => c.status !== 'completed').map((course) => (
                      <SelectItem key={course.course_id} value={course.course_id}>
                        <div className="flex items-center gap-2">
                          <span>{course.name}</span>
                          <span className="text-xs text-slate-400">
                            ({course.enrolled_count || 0}/{course.capacity})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
              <Textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="Any notes about the registration..."
                className="rounded-xl resize-none min-h-[80px]"
              />
            </div>

            {(!eligibilityPassed || !testPassed) && (
              <div className="p-3 bg-red-50 rounded-xl flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-600">
                  This student will be marked as not eligible and won't be enrolled. They'll need to rebook.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setCompleteDialog(false)}>
              Cancel
            </Button>
            <Button
              className={`rounded-xl ${eligibilityPassed && testPassed ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
              onClick={handleCompleteRegistration}
              disabled={completing || (eligibilityPassed && testPassed && !selectedCourseId)}
            >
              {completing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : eligibilityPassed && testPassed ? (
                <UserCheck className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {eligibilityPassed && testPassed ? 'Enrol Student' : 'Mark Not Eligible'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Registration Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Book Registration</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">Manually book a student in for registration</p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Student</label>
              {addRegForm.lead_id ? (
                <div className="flex items-center justify-between p-3 bg-pink-50 rounded-xl">
                  <span className="text-sm font-medium text-slate-900">{addRegForm.lead_name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-slate-500"
                    onClick={() => { setAddRegForm(p => ({ ...p, lead_id: '', lead_name: '' })); setLeadSearch(''); }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    placeholder="Search by name or phone..."
                    className="pl-9 rounded-xl"
                  />
                  {(leadResults.length > 0 || searchingLeads) && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {searchingLeads && <p className="p-3 text-xs text-slate-400">Searching...</p>}
                      {leadResults.map(lead => (
                        <button
                          key={lead.lead_id}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm transition-colors"
                          onClick={() => {
                            setAddRegForm(p => ({ ...p, lead_id: lead.lead_id, lead_name: lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() }));
                            setLeadSearch('');
                            setLeadResults([]);
                          }}
                        >
                          <p className="font-medium text-slate-900">{lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim()}</p>
                          <p className="text-xs text-slate-400">{lead.phone || lead.email || ''}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Date</label>
                <Input
                  type="date"
                  value={addRegForm.registration_date}
                  onChange={(e) => setAddRegForm(p => ({ ...p, registration_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Time (optional)</label>
                <Input
                  type="time"
                  value={addRegForm.registration_time}
                  onChange={(e) => setAddRegForm(p => ({ ...p, registration_time: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Course Interest (optional)</label>
              <Select value={addRegForm.course_interest} onValueChange={(v) => setAddRegForm(p => ({ ...p, course_interest: v }))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a course..." />
                </SelectTrigger>
                <SelectContent>
                  {courses.filter(c => c.status !== 'completed').map(c => (
                    <SelectItem key={c.course_id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
              <Textarea
                value={addRegForm.notes}
                onChange={(e) => setAddRegForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any notes..."
                className="rounded-xl resize-none min-h-[60px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button
              className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white"
              onClick={handleAddRegistration}
              disabled={addingReg || !addRegForm.lead_id || !addRegForm.registration_date}
            >
              {addingReg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarCheck className="w-4 h-4 mr-2" />}
              Book Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClassDetailDialog cls={classDetail} onClose={() => setClassDetail(null)} onOpenStudent={(id) => { setClassDetail(null); setDrawerLeadId(id); setDrawerOpen(true); }} />

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        leadId={drawerLeadId}
        onLeadUpdated={fetchRegistrations}
      />
    </div>
  );
};

export default RegistrationsPage;
